import React from 'react';
import { useUserSettings } from '@/hooks/useUserSettings';
import { Database } from 'lucide-react';
import { PartnrMark } from '@/components/partnr/kit/PartnrMark';

export function ProfileUsage() {
  const { settings, isLoading } = useUserSettings();

  if (isLoading) {
    // Same shape as the loaded section, so the menu doesn't jump when the numbers arrive
    return (
      <div className="px-5 py-4 space-y-4" aria-busy="true" aria-label="Loading usage">
        {[0, 1].map((i) => (
          <div key={i} className="space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-muted animate-pulse" />
                <div className="h-3.5 w-16 rounded bg-muted animate-pulse" />
              </div>
              <div className="h-3.5 w-14 rounded bg-muted animate-pulse" />
            </div>
            <div className="h-1.5 bg-muted rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (!settings) {
    return null;
  }

  const storagePercent = (settings.storage_used_mb / settings.storage_limit_mb) * 100;
  const aiPercent = (settings.ai_requests_used / settings.ai_requests_limit) * 100;

  const getBarColor = (percent: number, isAI = false) => {
    if (percent > 90) return 'bg-destructive';
    if (percent > 70) return 'bg-danger/60';
    return isAI ? 'bg-partnr' : 'bg-success';
  };

  return (
    <div className="px-5 py-4 space-y-4">
      {/* Storage */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-success/10 flex items-center justify-center">
              <Database className="h-3.5 w-3.5 text-success" />
            </div>
            <span className="text-sm font-medium text-foreground">Storage</span>
          </div>
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{settings.storage_used_mb.toFixed(1)}</span>
            <span className="text-xs"> / {settings.storage_limit_mb}MB</span>
          </span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${getBarColor(storagePercent)}`}
            style={{ width: `${Math.min(storagePercent, 100)}%` }}
          />
        </div>
      </div>
      
      {/* AI Requests */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-partnr/10 flex items-center justify-center">
              <PartnrMark size={14} className="text-partnr" />
            </div>
            <span className="text-sm font-medium text-foreground">Partnr AI</span>
          </div>
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{settings.ai_requests_used}</span>
            <span className="text-xs"> / {settings.ai_requests_limit}</span>
          </span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${getBarColor(aiPercent, true)}`}
            style={{ width: `${Math.min(aiPercent, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}
